import React, { useMemo } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
    TextInput, Select, Title, Paragraph,
    RadioButtonGroup,
    CheckboxGroup,
    Loader,
} from 'ui-library';

import { isQRIban } from 'utils';
import { BankInformationEnum } from 'datasource/CaseManagement/CaseManagement.types';
import './BankInformation.css';

const BankInformation = ({ datasource }) => {
    const { t } = useTranslation();
    const {
        form: {
            control, data, errors, clearErrors, trigger,
        },
        bankAccountOptions,
        showAllFieldsForKuDi,
        isJA,
        isPOA,
        isPK,
        isPA,
        active3bPortfolios,
        beneficiaryOptions,
        isActive3bPortfoliosLoading,
        accountDetailsIban,
        accountDetails,
        accountDetailsZurichDepots,
        onBankAccountChange,
        onActive3bAccountChange,
        onBeneficiaryChange,
        isPaymentOrderPrivateAccount,
        handleIBanOnBlur,
    } = datasource();

    const yesNoOption = useMemo(() => ([
        { label: t('clientDashboard.yes'), value: true, position: 1 },
        { label: t('clientDashboard.no'), value: false, position: 2 },
    ]), [t]);

    return (
        <form className="bank-information-form">
            {isActive3bPortfoliosLoading ? <Loader /> : (
                <Controller
                    name="bankAccountOption"
                    control={control}
                    render={({ field }) => (
                        <Select
                            {...field}
                            onChange={onBankAccountChange}
                            options={bankAccountOptions}
                            placeholder={t('position.pleaseSelect')}
                            data-cy="bankAccountOption"
                            error={t(errors?.bankAccountOption?.message || '')}
                            required
                            style={{ maxWidth: '400px' }}
                        />
                    )}
                />
            )}

            {data?.bankAccountOption && (
                <>
                    <div className="bank-information-form--beneficiary-info">
                        <Title type="3" className="bank-information-form--beneficiary-info-title">
                            {t('CNC.BankInfo.beneficiaryInfo.title')}
                        </Title>

                        {!showAllFieldsForKuDi && !isPA && (isJA || isPOA) && data?.bankAccountOption !== BankInformationEnum.Value5701 && (
                            <div className="bank-information-form--ja-beneficiary-options">
                                <Controller
                                    name="beneficiaryOption"
                                    control={control}
                                    render={({ field }) => (
                                        <RadioButtonGroup
                                            {...field}
                                            value={data?.beneficiaryOption != null ? +data?.beneficiaryOption : null}
                                            options={beneficiaryOptions}
                                            error={errors?.beneficiaryOption?.message}
                                            onChange={event => onBeneficiaryChange(+event?.target?.value)}
                                            required
                                        />
                                    )}
                                />
                            </div>
                        )}

                        {showAllFieldsForKuDi && (
                            <div className="form">
                                <Controller
                                    name="firstName"
                                    control={control}
                                    render={({ field }) => (
                                        <TextInput
                                            {...field}
                                            value={data?.firstName}
                                            data-cy="firstName"
                                            label={t('clientDetails.firstName')}
                                            error={errors?.firstName?.message}
                                            required
                                        />
                                    )}
                                />

                                <Controller
                                    name="lastName"
                                    control={control}
                                    render={({ field }) => (
                                        <TextInput
                                            {...field}
                                            data-cy="lastName"
                                            value={data?.lastName}
                                            label={t('clientDetails.lastName')}
                                            error={errors?.lastName?.message}
                                            required
                                        />
                                    )}
                                />

                                <Controller
                                    name="address"
                                    control={control}
                                    render={({ field }) => (
                                        <TextInput
                                            {...field}
                                            data-cy="address"
                                            value={data?.address}
                                            label={t('CNC.BankInfo.address')}
                                            error={errors?.address?.message}
                                        />
                                    )}
                                />

                                <div className="change-withdrawal-options-bankZip">
                                    <div className="form-group">
                                        <label htmlFor="zipCode" className="Form-control-label">{`${t('3b.withdrawalPlan.bankZipCode')}/${t('portfolio.createNewPortfolio.personalPage.connection.city')} *`}</label>
                                        <div className="change-withdrawal-options-bankZip-inputs">
                                            <Controller
                                                name="zipCodeOfBeneficiary"
                                                control={control}
                                                render={({ field: { onChange, ...field } }) => (
                                                    <TextInput
                                                        {...field}
                                                        value={data?.zipCodeOfBeneficiary}
                                                        data-cy="zipCodeOfBeneficiary"
                                                        id="zipCodeOfBeneficiary"
                                                        error={errors?.zipCodeOfBeneficiary?.message}
                                                        onChange={(value: number) => {
                                                            onChange(value);
                                                            trigger('zipCodeOfBeneficiary');
                                                        }}
                                                        required
                                                    />
                                                )}
                                            />
                                            <Controller
                                                name="cityOfBeneficiary"
                                                control={control}
                                                render={({ field }) => (
                                                    <TextInput
                                                        {...field}
                                                        value={data?.cityOfBeneficiary}
                                                        data-cy="cityOfBeneficiary"
                                                        error={errors?.cityOfBeneficiary?.message}
                                                        required
                                                    />
                                                )}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {!showAllFieldsForKuDi && (!(isJA || isPOA) || isPA || ((isJA || isPOA) && data?.bankAccountOption === BankInformationEnum.Value5701)) && (
                            <Paragraph className="bank-information-form--beneficiary-info-description">
                                <div>{`${data?.firstName} ${data?.lastName}`}</div>
                                <div>{`${data?.address ? `${data?.address}, ` : ''} ${data?.zipCodeOfBeneficiary} ${data?.cityOfBeneficiary}` }</div>
                            </Paragraph>
                        )}

                    </div>

                    <div className="form">
                        <Title type="3" className="bank-information-form--beneficiary-info-title">
                            {t('CNC.BankInfo.beneficiaryInfo.AccountDetails.title')}
                        </Title>

                        {data?.bankAccountOption === BankInformationEnum.Value5702 && (
                            <div className="mb-32">
                                {isActive3bPortfoliosLoading ? <Loader /> : (
                                    <Controller
                                        name="zurichInvestDepotAccount"
                                        control={control}
                                        render={({ field }) => (
                                            <Select
                                                {...field}
                                                onChange={onActive3bAccountChange}
                                                options={active3bPortfolios}
                                                placeholder={t('CNC.BankInfo.accountDetails.Label')}
                                                data-cy="zurichInvestDepotAccount"
                                                error={t(errors?.zurichInvestDepotAccount?.message || '')}
                                                required
                                            />
                                        )}
                                    />
                                )}
                            </div>
                        )}

                        {((data?.bankAccountOption === BankInformationEnum.Value5701)) && (
                            <Paragraph className="bank-information-form--beneficiary-info-description">
                                <div>{accountDetails?.bankName}</div>
                                <div>{`${accountDetails?.zipCode} ${accountDetails?.city}`}</div>
                            </Paragraph>
                        )}

                        {((data?.zurichInvestDepotAccount && data?.bankAccountOption === BankInformationEnum.Value5702)) && (
                            <Paragraph className="bank-information-form--beneficiary-info-description">
                                <div>{accountDetailsZurichDepots?.bankName}</div>
                                <div>{`${data?.zipCode} ${data?.city}` }</div>
                                {data?.zurichInvestDepotAccount && data?.bankAccountOption === BankInformationEnum.Value5702 && <div>{accountDetailsIban}</div>}
                            </Paragraph>
                        )}

                        {/* Hidden for Zurich Invest Depots */}
                        {data?.bankAccountOption !== BankInformationEnum.Value5702 && (
                            <>
                                <Controller
                                    name="iban"
                                    control={control}
                                    render={({ field: { onChange, ...field } }) => (
                                        <TextInput
                                            {...field}
                                            data-cy="iban"
                                            label={t('3b.withdrawalPlan.bankIban')}
                                            value={data?.iban}
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                onChange(e.target.value);
                                                clearErrors('iban');
                                            }}
                                            error={errors?.iban?.message}
                                            onBlur={handleIBanOnBlur}
                                            required
                                        />
                                    )}
                                />
                                <Controller
                                    name="esr"
                                    control={control}
                                    render={({ field: { onChange, ...field } }) => (
                                        <TextInput
                                            {...field}
                                            data-cy="esr"
                                            label={t('3b.withdrawalPlan.esr')}
                                            value={data?.esr}
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                onChange(e.target.value);
                                                clearErrors('esr');
                                            }}
                                            error={t(errors?.esr?.message)}
                                            required={isQRIban(data?.iban)}
                                            labelInfo={t('3b.withdrawalPlan.esr.info')}
                                        />
                                    )}
                                />
                            </>
                        )}

                        {data?.bankAccountOption === BankInformationEnum.Value5700 && (
                            <>
                                <Controller
                                    name="bankName"
                                    control={control}
                                    render={({ field }) => (
                                        <TextInput
                                            {...field}
                                            data-cy="bankName"
                                            value={data?.bankName}
                                            label={t('3b.withdrawalPlan.bankName')}
                                            error={errors?.bankName?.message}
                                            required
                                        />
                                    )}
                                />

                                <div className="change-withdrawal-options-bankZip">
                                    <div className="form-group">
                                        <label htmlFor="zipCode" className="Form-control-label">{`${t('3b.withdrawalPlan.bankZipCode')}/${t('3b.withdrawalPlan.bankCity')} *`}</label>
                                        <div className="change-withdrawal-options-bankZip-inputs">
                                            <Controller
                                                name="zipCode"
                                                control={control}
                                                render={({ field: { onChange, ...field } }) => (
                                                    <TextInput
                                                        {...field}
                                                        value={data?.zipCode}
                                                        data-cy="zipCode"
                                                        id="zipCode"
                                                        error={errors?.zipCode?.message}
                                                        onChange={(value: number) => {
                                                            onChange(value);
                                                            trigger('zipCode');
                                                        }}
                                                        required
                                                    />
                                                )}
                                            />
                                            <Controller
                                                name="city"
                                                control={control}
                                                render={({ field }) => (
                                                    <TextInput
                                                        {...field}
                                                        value={data?.city}
                                                        data-cy="city"
                                                        error={errors?.city?.message}
                                                        required
                                                    />
                                                )}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}

                        <Controller
                            name="messageText1"
                            control={control}
                            render={({ field }) => (
                                <TextInput
                                    {...field}
                                    value={data?.messageText1}
                                    data-cy="messageText1"
                                    label={!isPaymentOrderPrivateAccount ? t('CNC.BankInfo.messageText1').slice(0, -1) : t('CNC.BankInfo.messageText1')}
                                    error={errors?.messageText1?.message}
                                    maxLength={35}
                                />
                            )}
                        />

                        {isPaymentOrderPrivateAccount && (
                            <Controller
                                name="messageText2"
                                control={control}
                                render={({ field }) => (
                                    <TextInput
                                        {...field}
                                        value={data?.messageText2}
                                        data-cy="messageText2"
                                        label={t('CNC.BankInfo.messageText2')}
                                        error={errors?.messageText2?.message}
                                        maxLength={35}
                                    />
                                )}
                            />
                        )}

                        {isPK && (
                            <>
                                <Title type="3" className="bank-information-form--beneficiary-info-description mt-40 mb-0">
                                    <div>{t('CreateNewCase.Summary.Title.SubscriptionFeeConsiderationText.AdditionalText')}</div>
                                    <div>{t('CreateNewCase.Summary.Title.SubscriptionFeeConsiderationText.AdditionalText.Subtitle')}</div>
                                </Title>
                                <Title type="4" className="bank-information-form--beneficiary-info-title-small">
                                    {t('CreateNewCase.Summary.Title.SubscriptionFeeConsiderationText')}
                                </Title>
                                <Controller
                                    name="subscriptionFeeConsideration"
                                    control={control}
                                    render={({ field: { onChange, ...field } }) => (
                                        <CheckboxGroup
                                            {...field}
                                            value={data.subscriptionFeeConsideration ?? []}
                                            options={yesNoOption}
                                            error={<div>{errors?.subscriptionFeeConsideration?.message}</div>}
                                            size="big"
                                            onChange={onChange}
                                            defaultValue={[]}
                                            isConditional
                                            isInQuestionnaire
                                            required
                                        />
                                    )}
                                />
                            </>
                        )}
                    </div>
                </>
            )}
        </form>
    );
};

export default BankInformation;
